import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { UseOfForceReportModel } from '../../api-service/clients';
import { DatePicker, DatePickerValue } from '../../components/date-picker';
import { formatDate } from '../../utilities';
import { toDateTimeLocalValue } from '../citizen/serviceprovider/medicine/medicine-date-time';

interface UseOfForceReportSendingDialogProps {
  report: UseOfForceReportModel;
  onSave: (sentToSupervisionAt: Date, operatorInformedAt: string | undefined) => Promise<void>;
  onClose: () => void;
}

export function UseOfForceReportSendingDialog(props: UseOfForceReportSendingDialogProps) {
  const { report, onSave, onClose } = props;
  const [sentToSupervisionAt, setSentToSupervisionAt] = useState(() => toDateTimeLocalValue(new Date()));
  const [operatorInformedAt, setOperatorInformedAt] = useState<DatePickerValue>(() => new Date());
  const [isSaving, setIsSaving] = useState(false);

  const canSave = sentToSupervisionAt !== '' && (!report.requiresOperator || operatorInformedAt instanceof Date);

  async function save() {
    setIsSaving(true);
    try {
      await onSave(new Date(sentToSupervisionAt), report.requiresOperator ? formatDate(operatorInformedAt, 'yyyy-MM-dd') : undefined);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onClose={() => (isSaving ? undefined : onClose())} fullWidth maxWidth="sm">
      <DialogTitle>Registrér afsendelse til tilsynet</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2">Når afsendelsen er registreret, låses indberetningen, og skemaerne markeres som indberettet.</Typography>
          <TextField
            label="Sendt til tilsynet (dato og kl.)"
            type="datetime-local"
            value={sentToSupervisionAt}
            onChange={event => setSentToSupervisionAt(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {report.requiresOperator && (
            <DatePicker label="Driftsherren orienteret (dato)" value={operatorInformedAt} onChange={setOperatorInformedAt} maxDate={new Date()} variant="outlined" required />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          Afbryd
        </Button>
        <Button variant="contained" onClick={save} disabled={isSaving || !canSave}>
          Registrér afsendelse
        </Button>
      </DialogActions>
    </Dialog>
  );
}
